import React, { useState, useRef } from 'react';
import * as api from '../services/apiService';

const XteGenerator = ({ session, supabase }) => {
  const [excelFile, setExcelFile] = useState(null);
  const [loading, setLoading] = useState({
    processing: false,
    downloadingTemplate: false,
    downloadingXte: false,
    downloadingXml: false,
  });
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [processingStatus, setProcessingStatus] = useState({
    step1: { status: 'pending', message: 'Aguardando...' },
    step2: { status: 'pending', message: 'Pendente...' },
    step3: { status: 'pending', message: 'Pendente...' }
  });

  const fileRef = useRef(null);

  const getAuthToken = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Sessão expirada. Por favor, faça login novamente.');
    }
    return session.access_token;
  };

  const downloadFile = (blob, defaultFilename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = defaultFilename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  };

  const resetState = () => {
    setError('');
    setSuccessMessage('');
    setResults(null);
    setProcessingStatus({
      step1: { status: 'pending', message: 'Aguardando...' },
      step2: { status: 'pending', message: 'Pendente...' },
      step3: { status: 'pending', message: 'Pendente...' }
    });
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setExcelFile(file);
    resetState();
  };

  const handleDrag = (active) => {
    setDragActive(active);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      setExcelFile(file);
      resetState();
    }
  };

  const clearFiles = () => {
    setExcelFile(null);
    if (fileRef.current) fileRef.current.value = '';
    resetState();
  };

  const handleDownloadTemplate = async () => {
    setLoading(prev => ({ ...prev, downloadingTemplate: true }));
    try {
      const token = await getAuthToken();
      const blob = await api.downloadExcelTemplateAPI(token);
      downloadFile(blob, `template_gerador_TISS_${Date.now()}.xlsx`);
      showSuccess('Template baixado com sucesso!');
    } catch (error) {
      setError(error.message || 'Erro ao baixar template');
    } finally {
      setLoading(prev => ({ ...prev, downloadingTemplate: false }));
    }
  };

  const handleGenerate = async () => {
    if (!excelFile) {
      setError('Por favor, selecione um arquivo Excel.');
      return;
    }

    resetState();
    setLoading(prev => ({ ...prev, processing: true }));

    try {
      const token = await getAuthToken();

      setProcessingStatus(prev => ({
        ...prev,
        step1: { status: 'processing', message: 'Lendo planilha...' }
      }));

      const data = await api.generateXteFromExcelAPI(excelFile, token);

      setProcessingStatus(prev => ({
        ...prev,
        step1: { status: 'success', message: 'Planilha lida com sucesso.' },
        step2: { status: 'success', message: `${data.data.xml_files_count} XML e ${data.data.xte_files_count} XTE criados.` },
        step3: { status: 'success', message: 'Arquivos .ZIP prontos.' }
      }));

      setResults(data);
      showSuccess('Arquivos gerados com sucesso!');

    } catch (error) {
      setError(error.message || 'Erro ao gerar arquivos');
      setProcessingStatus(prev => ({
        ...prev,
        step1: { status: 'error', message: 'Erro ao processar planilha' }
      }));
    } finally {
      setLoading(prev => ({ ...prev, processing: false }));
    }
  };

  const handleDownloadXte = async () => {
    setLoading(prev => ({ ...prev, downloadingXte: true }));
    try {
      const token = await getAuthToken();
      const blob = await api.generateAndDownloadXteZipAPI(excelFile, token);
      downloadFile(blob, `arquivos_xte_${Date.now()}.zip`);
      showSuccess('Arquivos XTE baixados com sucesso!');
    } catch (error) {
      setError(error.message || 'Erro ao gerar arquivos XTE');
    } finally {
      setLoading(prev => ({ ...prev, downloadingXte: false }));
    }
  };

  const handleDownloadXml = async () => {
    setLoading(prev => ({ ...prev, downloadingXml: true }));
    try {
      const token = await getAuthToken();
      const blob = await api.generateAndDownloadXmlZipAPI(excelFile, token);
      downloadFile(blob, `arquivos_xml_${Date.now()}.zip`);
      showSuccess('Arquivos XML baixados com sucesso!');
    } catch (error) {
      setError(error.message || 'Erro ao gerar arquivos XML');
    } finally {
      setLoading(prev => ({ ...prev, downloadingXml: false }));
    }
  };

  const isAnyLoading = Object.values(loading).some(Boolean);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {(error || successMessage) && (
        <div className="fixed top-24 right-4 z-50 max-w-md animate-slide-in-right">
          {error && (
            <div className="alert-error shadow-xl">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-semibold">Erro</p>
                  <p className="text-sm">{error}</p>
                </div>
                <button onClick={() => setError('')} className="ml-auto">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          {successMessage && (
            <div className="alert-success shadow-xl">
              <div className="flex items-start gap-3">
                <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="font-semibold">Sucesso</p>
                  <p className="text-sm">{successMessage}</p>
                </div>
                <button onClick={() => setSuccessMessage('')} className="ml-auto">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card-glass p-8 text-center">
        <div className="inline-block p-4 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl mb-4">
          <svg className="w-16 h-16 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </div>
        <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3">
          Gerar Envios TISS
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Crie arquivos .XTE e .XML a partir de uma planilha Excel - ideal para reenvios após correções
        </p>
      </div>



      <div className="card p-8">
        <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
          </svg>
          Carregar Planilha Preenchida
        </h3>

        <div
          className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all duration-300 ${
            dragActive 
              ? 'border-purple-500 bg-purple-100 scale-105' 
              : 'border-gray-300 hover:border-purple-400 hover:bg-purple-50'
          }`}
          onDragEnter={() => handleDrag(true)}
          onDragLeave={() => handleDrag(false)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileChange}
            className="hidden"
            id="excel-file"
          />
          <label htmlFor="excel-file" className="cursor-pointer">
            <div className="mx-auto w-24 h-24 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-2xl flex items-center justify-center mb-6">
              <svg className="w-12 h-12 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-gray-700 mb-2">
              Arraste o arquivo aqui ou clique para selecionar
            </p>
            <p className="text-sm text-gray-500">
              Formatos aceitos: <span className="font-semibold">.XLSX, .XLS, .CSV</span>
            </p>
          </label>
        </div>

        {excelFile && (
          <div className="mt-6 animate-scale-in">
            <div className="flex items-center justify-between mb-4">
              <p className="font-semibold text-gray-800">Arquivo selecionado:</p>
              <button
                onClick={clearFiles}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-2 group"
              >
                <svg className="w-4 h-4 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Remover
              </button>
            </div>
            <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 truncate">{excelFile.name}</p>
                <p className="text-sm text-gray-600">{(excelFile.size / 1024).toFixed(2)} KB</p>
              </div>
              <div className="badge-success">Excel</div>
            </div>
          </div>
        )}

        <div className="mt-6">
          <button
            onClick={handleGenerate}
            disabled={isAnyLoading || !excelFile}
            className="btn btn-primary btn-lg w-full"
          >
            {loading.processing ? (
              <>
                <div className="loading-spinner" />
                <span>Gerando arquivos...</span>
              </>
            ) : (
              <>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Gerar Arquivos TISS</span>
              </>
            )}
          </button>
        </div>
      </div>

      {(loading.processing || results) && (
        <div />
      )}

      {results && (
        <div className="space-y-6 animate-scale-in">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="card p-8 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-3xl">📁</span>
                </div>
                <div>
                  <p className="text-4xl font-bold text-blue-600">{results.data.total_files}</p>
                  <p className="text-sm font-medium text-blue-700">Arquivos Gerados</p>
                </div>
              </div>
            </div>
            <div className="card p-8 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                  <span className="text-3xl">📤</span>
                </div>
                <div>
                  <p className="text-4xl font-bold text-green-600">{results.data.xte_files_count}</p>
                  <p className="text-sm font-medium text-green-700">Arquivos XTE</p>
                </div>
              </div>
            </div>
          </div>

          <div className="card p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Downloads Disponíveis
            </h3>
            <div className="grid md:grid-cols-1 gap-4">
              <button
                onClick={handleDownloadXte}
                disabled={isAnyLoading || results.data.xte_files_count === 0}
                className="btn btn-success btn-lg"
              >
                {loading.downloadingXte ? (
                  <>
                    <div className="loading-spinner" />
                    <span>Gerando ZIP...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span>Baixar .XTE ({results.data.xte_files_count} arquivos)</span>
                  </>
                )}
              </button>

            </div>
          </div>

          {results.data.file_names && results.data.file_names.length > 0 && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-800 flex items-center gap-2">
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Arquivos Gerados ({results.data.file_names.length})
              </h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto">
                {results.data.file_names.map((fileName, idx) => (
                  <div key={idx} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3 hover:bg-gray-100 transition-colors">
                    <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <span className="text-sm text-gray-700 truncate">{fileName}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="card p-6 bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-orange-900 mb-3">Dicas Importantes</h3>
            <ul className="space-y-2 text-sm text-orange-800">
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span>A coluna "Nome da Origem" define o nome do arquivo que será gerado</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span>Datas devem estar no formato DD/MM/AAAA ou AAAA-MM-DD</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span>Valores monetários devem ser numéricos (sem símbolos de moeda)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span>Campos obrigatórios: Nome da Origem, numeroGuia_prestador, numeroGuia_operadora</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-orange-500 mt-0.5">•</span>
                <span>Arquivos .XTE são para envio à ANS, .XML são para visualização</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusCard = ({ title, status, message, icon }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'success': return 'status-card-success';
      case 'processing': return 'status-card-processing';
      case 'error': return 'status-card-error';
      default: return 'status-card-pending';
    }
  };

  const getIcon = () => {
    switch (status) {
      case 'success': return '✅';
      case 'processing': return '⏳';
      case 'error': return '❌';
      default: return icon;
    }
  };

  return (
    <div className={`${getStatusColor()} p-6`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="text-3xl">{getIcon()}</span>
        <h4 className="font-bold text-gray-800">{title}</h4>
      </div>
      <p className="text-sm text-gray-600">{message}</p>
    </div>
  );
};

export default XteGenerator;
